import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import {
  BILLING_CYCLES,
  PAYABLE_PLAN_NAMES,
  formatPlanPrice,
  getPlanDisplayName,
  getStaticPaymentPlans,
  startRazorpayCheckout,
} from "../../services/razorpayService";
import { appStorage } from "../../utils/appStorage";
import usePaymentStatus from "../../hooks/usePaymentStatus";
import { forceAuthLogout, isSmsVerificationPending } from "../../utils/authSession";
import { isBasicPlanUser } from "../../utils/basicPlanAccess";
import oneSignalService from "../../services/oneSignalService";

function BillingToggle({ value, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-full border border-violet-200 bg-white/80 p-1 shadow-sm">
      {BILLING_CYCLES.map((cycle) => (
        <button
          key={cycle.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(cycle.id)}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
            value === cycle.id
              ? "bg-[#6938EF] text-white shadow-md"
              : "text-[#6938EF] hover:bg-violet-50"
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {cycle.label}
        </button>
      ))}
    </div>
  );
}

export default function Payment({ open, onClose } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans] = useState(getStaticPaymentPlans());
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState("Basic");
  const [paying, setPaying] = useState(false);
  const [showFreeModal, setShowFreeModal] = useState(false);

  const token = Cookies.get("auth_token");
  const { planType: currentPlanStatus, loading: paymentStatusLoading } = usePaymentStatus();
  const visiblePlans = useMemo(
    () =>
      currentPlanStatus > 0
        ? plans.filter((plan) => plan.plan_name !== "Basic")
        : plans,
    [plans, currentPlanStatus]
  );

  useEffect(() => {
    if (typeof open === "boolean" && open) setShowFreeModal(false);
  }, [open]);

  useEffect(() => {
    const requestedPlan = new URLSearchParams(location.search).get("plan");
    const canSelectRequested = visiblePlans.some(
      (plan) => plan.plan_name === requestedPlan
    );
    const canKeepSelected = visiblePlans.some(
      (plan) => plan.plan_name === selectedPlan
    );

    if (canSelectRequested) {
      setSelectedPlan(requestedPlan);
    } else if (!canKeepSelected) {
      setSelectedPlan(visiblePlans[0]?.plan_name || "");
    }
  }, [location.search, selectedPlan, visiblePlans]);

  const handlePlanClick = async (plan) => {
    setSelectedPlan(plan.plan_name);

    if (!plan.premium) {
      setShowFreeModal(true);
      return;
    }

    if (!PAYABLE_PLAN_NAMES.includes(plan.plan_name)) {
      toast.error("This plan is not available for online payment.");
      return;
    }

    if (!token) {
      toast.error("Please sign in first");
      navigate("/login", { replace: true });
      return;
    }

    setPaying(true);
    toast.dismiss();

    try {
      await startRazorpayCheckout({
        planName: plan.plan_name,
        billingCycle,
        user: {
          name: appStorage.getItem("user_name") || "",
          email: appStorage.getItem("user_email") || appStorage.getItem("email") || "",
          phone: appStorage.getItem("user_phone") || appStorage.getItem("phone_number") || "",
        },
        onVerified: () => {
          appStorage.setItem("payment_status", "1");
          appStorage.setItem("choose_plan_required", "0");
          appStorage.setItem("upgrade_plan_required", "0");
          toast.success("Payment verified successfully!");

          if (isSmsVerificationPending()) {
            appStorage.setItem("verification_type", "3");
            navigate("/otp", { replace: true });
          } else {
            navigate("/contact", { replace: true });
          }
        },
      });
    } catch (err) {
      if (err?.message !== "Payment cancelled") {
        toast.error(err?.message || "Payment failed");
      }
    } finally {
      setPaying(false);
    }
  };

  const closeFreeModal = () => {
    if (typeof onClose === "function") onClose();
    setShowFreeModal(false);
  };

  const handleLogout = () => {
    oneSignalService.removeExternalUserId().catch(() => {});
    forceAuthLogout();
    toast.success("Logged out successfully");
    navigate("/login", { replace: true });
  };

  const showLogoutButton = isBasicPlanUser() || currentPlanStatus === 1;
  const priceSuffix = billingCycle === "annual" ? "/ year" : "/ month";

  if (paymentStatusLoading) {
    return (
      <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AF8]" />
      </div>
    );
  }

  return (
    <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-8">
      <div className="relative w-full max-w-[1440px]">
        {showLogoutButton && (
          <button
            type="button"
            onClick={handleLogout}
            className="absolute right-0 top-0 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-[#6938EF] shadow-sm transition hover:bg-violet-50"
          >
            <img src="../chatbar/logout.svg" alt="" className="h-4 w-4" />
            Logout
          </button>
        )}

        <div className="flex flex-col items-center gap-4 mb-2">
          <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
            <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
             PhoneBookPlus
          </h1>
        </div>

        <div className="space-y-3 w-full">
          <div className="text-2xl text-[#475467] font-medium text-center">Choose Your Plan</div>
          <p className="text-[#475467] font-medium text-center">
            Pick {getPlanDisplayName("Basic Plus")} for essential premium tools or {getPlanDisplayName("Basic Pro")} for full access.
          </p>

          <div className="flex justify-center">
            <BillingToggle value={billingCycle} onChange={setBillingCycle} disabled={paying} />
          </div>

          {!token && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900">
              Please sign in to complete payment.
            </div>
          )}

          {visiblePlans.length === 0 ? (
            <div className="rounded-3xl border border-violet-200 bg-white p-6 text-center shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900">You are already on the highest plan.</h2>
              <p className="mt-2 text-sm text-gray-600">
                No upgrade plan is available for your current subscription.
              </p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
              visiblePlans.length > 2 ? "lg:grid-cols-3" : "lg:grid-cols-2"
            }`}>
              {visiblePlans.map((plan) => {
              const isSelected = selectedPlan === plan.plan_name;
              const isPopular = plan.plan_name === "Basic Plus";
              const price = plan.premium ? formatPlanPrice(plan, billingCycle) : plan.priceLabel;

              return (
                <div
                  key={plan.plan_name}
                  className={`relative rounded-3xl border p-5 sm:p-6 shadow-sm transition ${
                    isSelected ? "border-[#7A5AF8] bg-white shadow-lg" : "border-gray-200 bg-white"
                  }`}
                >
                  {isPopular && (
                    <span className="absolute top-[-10px] border-[#7A5AF8] border mb-3 inline-flex items-center gap-1 rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-[#7C4DFF]">
                      Most Popular
                    </span>
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900">{getPlanDisplayName(plan)}</h3>
                      <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#101828]">{price}</div>
                      {plan.premium && (
                        <div className="text-xs text-gray-500">{priceSuffix}</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="inline-flex flex-shrink-0 h-5 w-5 items-center justify-center rounded-full bg-[#7A5AF8] text-white text-xs">
                          ✓
                        </span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    disabled={paying && isSelected}
                    className={`mt-5 flex w-full items-center justify-center rounded-xl px-6 py-3 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      plan.premium
                        ? "submit-button text-white"
                        : "border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                    }`}
                    onClick={() => handlePlanClick(plan)}
                  >
                    {paying && isSelected
                      ? "Opening Razorpay…"
                      : plan.premium
                      ? `Pay for ${getPlanDisplayName(plan)}`
                      : `Choose Free ${getPlanDisplayName("Basic")}`}
                  </button>
                </div>
              );
              })}
            </div>
          )}

          <p className="text-[#263238] text-center flex items-center justify-center gap-2 text-sm">
            {/* <svg className="h-4 w-4 text-[#7C4DFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            100% Secure Payment powered by Razorpay */}
             <img src="/info1.svg" alt="Info" className="h-4 w-4" /> Once cloud storage is full or the time limit expires, data will be safely stored locally on your device.

          </p>
          <p className="text-[#263238] text-center flex items-center justify-center gap-2 text-sm">
         <img src="/info1.svg" alt="Warning" className="h-4 w-4" /> Features and plan details are subject to change.</p>
        </div>
      </div>

      {showFreeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={closeFreeModal}
        >
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[2rem] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeFreeModal}
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-gray-500 shadow-sm transition hover:bg-white hover:text-gray-900"
              aria-label="Close"
            >
              X
            </button>

            <div className="bg-gradient-to-br from-[#7C4DFF] via-[#6A35FF] to-[#40248B] px-6 pb-10 pt-9 text-center text-white">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
                <img src="./logo.svg" alt=" PhoneBookPlus Logo" className="h-10 w-10" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                {getPlanDisplayName("Basic")} Plan
              </p>
              <h3 className="mt-2 text-2xl font-bold">Mobile App Only</h3>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-white/85">
                 PhoneBookPlus web access is available for {getPlanDisplayName("Basic Plus")} and {getPlanDisplayName("Basic Pro")} users.
                {getPlanDisplayName("Basic")} plan users can continue from our mobile app.
              </p>
            </div>

            <div className="-mt-6 px-6 pb-6">
              <div className="rounded-3xl border border-violet-100 bg-white p-5 text-center shadow-xl">
                <p className="text-sm leading-6 text-gray-600">
                  Download  PhoneBookPlus from the Play Store or App Store on your mobile device.
                </p>

                <div className="mt-5 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Want to use  PhoneBookPlus on web? Please choose {getPlanDisplayName("Basic Plus")} or {getPlanDisplayName("Basic Pro")}.
                </div>

                <button
                  type="button"
                  onClick={closeFreeModal}
                  className="mt-5 w-full rounded-full bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3 font-semibold text-white shadow-lg shadow-[#7C4DFF]/25 transition hover:brightness-105"
                >
                  Choose {getPlanDisplayName("Basic Plus")} or {getPlanDisplayName("Basic Pro")}
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-gray-500">
                You will not be logged out. Select a paid plan to continue on web.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
